import { create } from "zustand";
import {
  getMonthlySummary,
  releaseSalary as releaseSalaryApi,
  getBanner as getBannerApi
} from "@/services/HrService/MonthlySalaryService";
import { registerStoreReset } from "@/store/storeResetUtils";

export interface MonthlyEmployeeRow {
  employee_id: string;
  name: string;
  email: string;
  role_name: string;
  total_punch_ins: number;
  total_punch_outs: number;
  release_status: 'released' | null;
  released_at: string | null;
  released_by: string | null;
  released_by_name: string | null;
  released: boolean;
}

export interface MonthlyPeriod {
  year: number;
  month: number;
}

interface MonthlySalaryStore {
  period: MonthlyPeriod | null;
  employees: MonthlyEmployeeRow[];
  banner: { show: boolean; period: MonthlyPeriod } | null;
  loading: boolean;
  releasingId: string | null;
  error: string | null;

  fetchSummary: (year?: number, month?: number) => Promise<void>;
  fetchBanner: () => Promise<void>;
  release: (employeeId: string, year: number, month: number) => Promise<boolean>;
  resetStore: () => void;
}

const useMonthlySalaryStore = create<MonthlySalaryStore>((set, get) => ({
  period: null,
  employees: [],
  banner: null,
  loading: false,
  releasingId: null,
  error: null,

  fetchSummary: async (year?: number, month?: number) => {
    try {
      set({ loading: true, error: null });
      const response = await getMonthlySummary(year, month);
      if (response.success && response.data) {
        set({
          period: response.data.period,
          employees: response.data.employees || [],
          loading: false
        });
      } else {
        set({ error: response.error?.message || 'Failed to load summary', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  fetchBanner: async () => {
    try {
      const response = await getBannerApi();
      if (response.success) {
        set({ banner: response.data });
      }
    } catch {
      // Non-critical — banner just won't show.
    }
  },

  release: async (employeeId: string, year: number, month: number) => {
    try {
      set({ releasingId: employeeId, error: null });
      const response = await releaseSalaryApi(employeeId, year, month);
      if (response.success) {
        // Reflect the release locally without a full refetch.
        set({
          releasingId: null,
          employees: get().employees.map((e) =>
            e.employee_id === employeeId
              ? { ...e, released: true, release_status: 'released' }
              : e
          )
        });
        return true;
      }
      set({ error: response.error?.message || 'Failed to release salary', releasingId: null });
      return false;
    } catch (error: any) {
      set({ error: error.message, releasingId: null });
      return false;
    }
  },

  resetStore: () =>
    set({
      period: null,
      employees: [],
      banner: null,
      loading: false,
      releasingId: null,
      error: null
    })
}));

registerStoreReset('monthlySalary', () => useMonthlySalaryStore.getState().resetStore());

export default useMonthlySalaryStore;
